import React, { useEffect, useMemo, useState } from 'react'
import StarterPage from './components/StarterPage'
import { useMutation, useQuery } from 'react-query';
import { api as courses } from '../../../../../../api/courses'; 
import { Link } from 'react-router-dom';
import EmptyPlaceHolder from '../../../../../../components/EmptyPlaceHolder';
import Table from '../../../../../../components/table/Table';
import Pagination from '../../../../../../components/pagination/Pagination';
import PermissionGate from '../../../../../../components/PermissionGate';
import CenteredModal from '../../../../../../components/Modal/CenteredModal';
import Confirmation from '../../../../components/ConfimationModal';
import toast from 'react-hot-toast';

const ViewModules = () => {

    const [perPage, setPerPage] = useState<number>(15);
    const [search, setSearch] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isEmpty, setIsEmpty] = useState<boolean>(false)
    const [touched, setTouched] = useState<boolean>(false)
    const [openPreview, setOpenPreview] = useState<boolean>(false)
    const [selectedModule, setSelectedModule] = useState<any>(null)
    const [previewUrl, setPreviewUrl] = useState<string>('')
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
    const [moduleToDelete, setModuleToDelete] = useState<any>(null)

    const normalizeType = (path?: string) => {
      const loweredPath = (path || '').toLowerCase();
      return loweredPath.endsWith('.pdf') ? 'ebook' : 'video';
    }

    const formatDuration = (value: any) => {
      if (!value && value !== 0) return '00:00';
      if (typeof value === 'string' && value.includes(':')) return value;

      const seconds = Number(value);
      if (Number.isNaN(seconds)) return '00:00';

      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;

      if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      }

      return `${minutes}:${String(secs).padStart(2, '0')}`;
    }

    const getDefaultFilePath = (module: any) => {
      const defaultLanguage = module?.languages?.find((lang: any) => lang?.isDefault);
      return defaultLanguage?.filePath || module?.languages?.[0]?.filePath || module?.filePath || '/content/modules/module.file';
    }

    const selectedFilePath = getDefaultFilePath(selectedModule)
    const selectedType = normalizeType(selectedFilePath)
    const selectedDuration = formatDuration(selectedModule?.duration)
  
    const { data: modules, isLoading, refetch } = useQuery(
      ["Modules",  currentPage, search, perPage ],
      () => {
        return courses.getModules({ currentPage, search, perPage });
      },
      {
        cacheTime: 10000,
        staleTime: 10000,
        refetchOnWindowFocus: true,
        select: (data: any) => data,
        onSuccess: (response: any) => {
            if(response?.meta?.total) {
                setIsEmpty(false)
                setTouched(true)
            }
            else setIsEmpty(true)
          }
      }
    );

    useEffect( () => {
        refetch()
    }, [])

    const deleteMutation = useMutation(courses.deleteModule)
    const previewMutation = useMutation(courses.previewModule, {
      onSuccess: (response: any) => {
        setPreviewUrl(response?.data?.url || '')
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to preview module')
      }
    })

    const confirmDelete = (module: any) => {
      setModuleToDelete(module)
      setOpenDeleteModal(true)
    }

    const handleDelete = async () => {
      if (!moduleToDelete?.id) return

      try {
        toast.loading('Deleting module...')
        const response: any = await deleteMutation.mutateAsync(moduleToDelete?.id)
        toast.remove()
        toast.success(response?.message || 'Module deleted successfully')
        setOpenDeleteModal(false)
        setModuleToDelete(null)
        refetch()
      } catch (error: any) {
        toast.remove()
        toast.error(error?.response?.data?.message || 'Failed to delete module')
      }
    }

    let columns = useMemo(
        () => [
          {
            title: "ID",
            selector: null,
            key: "id",
            render: ({ index } : {index: number}) =>
              index +
              1 +
              ((modules?.meta?.page || 1) - 1) *
                (modules?.meta?.perPage || perPage),
          },
          {
            title: "Course Name",
            selector: "name",
            key: "name",
          },
          {
            title: "Assessment",
            selector: "isAssessment",
            key: "isAssessment",
            render: ({ item } : {item: boolean} ) => {
              return <span 
                 className={item ? 'bi bi-check-circle text-primary-green text-sm pl-3' :
                    'bi bi-x-octagon text-red-500 text-sm pl-3'
                 }
               />
            },
          },
          {
            title: "Assessment Type",
            selector: "isAssessment",
            key: "isAssessment",
            render: ({ item, row } : {item: boolean, row: any} ) => {
              return  item ? <span>{row?.assessmentType}</span> : <EmptyPlaceHolder count={5} />
            },
          },
          {
            title: "Multi Language",
            selector: null,
            key: null,
            render: ({ row } : any) => {
              return <span 
              className={ row?.languages?.length > 1 ? 'bi bi-check-circle text-primary-green text-sm pl-3' :
                 'bi bi-x-octagon text-red-500 text-sm pl-3'
              }
             />
            },
          },
          {
            title: "Date Added",
            selector: "createdAt",
            key: "createdAt",
            render: ({ item }: { item : string }) => {
              return (
                <span className="">{`${new Date(item).toLocaleDateString(
                  "en-ng"
                )}`}</span>
              );
            },
          },
          {
            title: "Last Updated",
            selector: "updatedAt",
            key: "updatedAt",
            render: ({ item } : { item : string }) => {
              return (
                <span className="">{`${new Date(item).toLocaleDateString(
                  "en-ng"
                )}`}</span>
              );
            },
          },
          {
            title: "Action",
            selector: null,
            key: "actions",
            render: ({ row } : any) => {
              return (
                <div className='flex items-center gap-3'>
                  <PermissionGate slug="module-management.view">
                    <button
                      type='button'
                      className="bi bi-eye text-primary-blue text-lg"
                      onClick={() => {
                        setSelectedModule(row)
                        setOpenPreview(true)
                        setPreviewUrl('')
                        previewMutation.mutate({ id: row?.id, language: 'en', search: undefined })
                      }}
                    />
                  </PermissionGate>
                  <PermissionGate slug="module-management.update">
                    <Link
                      className="bi bi-pencil-square text-primary-blue text-lg"
                      to={`/admin/modules/edit/${row?.id}`}
                      onClick={() => {
                        sessionStorage.setItem("active-module", JSON.stringify(row));
                      }}
                    />
                  </PermissionGate>
                  <PermissionGate slug="module-management.delete">
                    <button
                      type='button'
                      className="bi bi-trash text-red-500 text-lg"
                      onClick={() => confirmDelete(row)}
                    />
                  </PermissionGate>
                </div>
              );
            },
          },
        ],
        [1, 20, 20]
      );
    
    const tableProps = { isLoading, columns, data: modules?.data };

  return (
    <div className='p-5'>  
        {
           (!isEmpty || touched) ?
              <section>
                 <header className='flex justify-between items-center mt-1 mb-5'>
                       <h5 className='text-md font-semibold'>
                         Total Modules  ({modules?.meta?.total})
                       </h5>
                        <div className='flex items-center'>
                          <input 
                            type="search" 
                            className='input-style bg-primaryGray w-[400px]'
                            placeholder='&#128269; Search modules....'
                            onChange={ (event: React.ChangeEvent<HTMLInputElement>) => {
                              setSearch(event.target.value)
                            }}
                          />
                           <PermissionGate slug="module-management.create">
                           <Link to={'/admin/modules/create'} 
                               className='btn pt-3 pb-3 pl-8 pr-8 bg-primary-blue ml-5'
                               onClick={ () => sessionStorage.removeItem('active-module') }
                             >
                                 Create New Module
                             </Link>
                           </PermissionGate>
                        </div>
                    </header>
                    <div className='p-5 bg-white rounded-xl'>
                      <Table {...tableProps} />
                      <Pagination
                        data={modules?.meta}
                        perPage={perPage}
                        pageNo={currentPage}
                        setPageNo={setCurrentPage}
                        setPerPage={setPerPage}
                    />
                    </div>
                </section>
              :
        <StarterPage />
        }

      <CenteredModal
        title={<h5 className='text-md font-semibold'>Preview: {selectedModule?.name || 'Module'}</h5>}
        bgColor={'bg-white'}
        open={openPreview}
        setOpen={setOpenPreview}
        style={{ padding: 0 }}
        width={'w-[620px]'}
        closeButtonLabel={'Close'}
        btnBgColor={'bg-primary-blue'}
        onClose={() => setOpenPreview(false)}
        headerClassName={'text-sm flex items-center justify-between leading-6 font-medium text-customBlue text-left'}
      >
        <section className='px-4 pb-4 pt-2'>
          {previewMutation.isLoading ? (
            <div className='rounded-xl bg-[#091A44] h-[260px] text-white flex items-center justify-center text-center p-4'>
              <p className='text-lg font-medium'>Loading module preview...</p>
            </div>
          ) : previewUrl ? (
            <iframe
              src={previewUrl}
              className='rounded-xl w-full h-[260px] border-0'
              title='Module Preview'
            />
          ) : selectedType === 'ebook' ? (
            <div className='rounded-xl border-2 border-dashed border-[#D9E2EC] bg-[#F8FAFC] h-[260px] text-[#64748B] flex items-center justify-center text-center p-4'>
              <div>
                <div className='w-16 h-16 mx-auto mb-4 rounded-full bg-[#E2E8F0] flex items-center justify-center'>
                  <span className='text-2xl bi bi-file-earmark-text'></span>
                </div>
                <h6 className='text-[32px] leading-none mb-2'>-</h6>
                <h6 className='text-[30px] leading-none mb-2'>-</h6>
                <p className='text-xl font-medium'>PDF Document</p>
                <p className='text-sm mt-2 break-all'>
                  {selectedFilePath}
                </p>
              </div>
            </div>
          ) : (
            <div className='rounded-xl bg-[#091A44] h-[260px] text-white flex items-center justify-center text-center p-4'>
              <div>
                <div className='w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center'>
                  <span className='text-2xl bi bi-camera-video'></span>
                </div>
                <p className='text-xl font-medium'>Video player placeholder</p>
                <p className='text-sm text-gray-300 mt-2 break-all'>
                  {selectedFilePath}
                </p>
              </div>
            </div>
          )}

          <div className='grid grid-cols-2 gap-3 mt-3'>
            <div className='bg-primaryGray rounded-md p-3'>
              <p className='text-sm text-gray-500'>Duration</p>
              <p className='font-semibold text-2xl leading-tight'>{selectedDuration}</p>
            </div>
            <div className='bg-primaryGray rounded-md p-3'>
              <p className='text-sm text-gray-500'>Type</p>
              <p className='font-semibold text-2xl leading-tight capitalize'>{selectedType}</p>
            </div>
          </div>
        </section>
      </CenteredModal>

      <Confirmation
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        title="Delete Module"
        text={`Are you sure you want to delete "${moduleToDelete?.name || ''}"? This action cannot be undone.`}
        width="w-[450px]"
        footerButtonsAlignment="end"
        footer={
          <>
            <button
              className="btn-light pl-8 pr-8 bg-primaryGray mr-3"
              onClick={() => {
                setOpenDeleteModal(false)
                setModuleToDelete(null)
              }}
            >
              Cancel
            </button>
            <button
              className="btn pl-8 pr-8 bg-red-500"
              onClick={handleDelete}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
            </button>
          </>
        }
      />
    </div>
  )
}

export default ViewModules